import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { feedApi, discoveryApi, usersApi, shelfApi } from '@/lib/api';
import AppNavbar from '@/components/app/AppNavbar';
import BookCard from '@/components/app/BookCard';
import UserCard from '@/components/app/UserCard';
import TimeAgo from '@/components/app/TimeAgo';
import StarRating from '@/components/app/StarRating';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BookOpen, Users, Compass, X, Sparkles } from 'lucide-react';
import MobileBottomNav from '@/components/app/MobileBottomNav';
import GoodreadsImportModal from '@/components/app/GoodreadsImportModal';
import TrendingBooks from '@/components/app/TrendingBooks';
import CultureFeedSection from '@/components/app/CultureFeedSection';
import { toast } from 'sonner';

const SHELF_LABELS = {
  read: 'Read',
  reading: 'Currently Reading',
  want: 'Want to Read',
  own: 'Own',
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function FeedItemSkeleton() {
  return (
    <div className="bg-cream border border-cream-dark rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-full bg-cream-dark" />
        <div className="space-y-1.5 flex-1">
          <Skeleton className="h-3.5 w-32 bg-cream-dark" />
          <Skeleton className="h-3 w-20 bg-cream-dark" />
        </div>
      </div>
      <div className="flex gap-3">
        <Skeleton className="w-12 h-16 rounded bg-cream-dark" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-48 bg-cream-dark" />
          <Skeleton className="h-3 w-32 bg-cream-dark" />
          <Skeleton className="h-3 w-full bg-cream-dark" />
        </div>
      </div>
    </div>
  );
}

function FeedItem({ item }) {
  const navigate = useNavigate();

  const book = item.book;
  const user = item.user;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-cream border border-cream-dark rounded-xl p-4 hover:border-terra/20 transition-colors"
    >
      {/* User info */}
      <div className="flex items-center gap-3 mb-3">
        <button onClick={() => navigate(`/profile/${user.username}`)}>
          <Avatar className="w-9 h-9">
            <AvatarImage src={user.avatar_url} alt={user.display_name} />
            <AvatarFallback className="bg-terra text-cream text-xs font-semibold">
              {getInitials(user.display_name || user.username)}
            </AvatarFallback>
          </Avatar>
        </button>
        <div className="flex-1 min-w-0">
          <button
            onClick={() => navigate(`/profile/${user.username}`)}
            className="text-sm font-semibold text-ink hover:text-terra transition-colors"
          >
            {user.display_name || user.username}
          </button>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-ink-muted">
              {item.type === 'shelf_add' && `added to ${SHELF_LABELS[item.shelf_state] || item.shelf_state} shelf`}
              {item.type === 'review' && 'wrote a review'}
              {item.type === 'reaction' && 'reacted'}
              {item.type === 'discussion' && 'posted in the discussion'}
              {item.type === 'discussion_reply' && 'replied to a discussion'}
            </span>
            <span className="text-ink-muted text-xs">·</span>
            <TimeAgo date={item.created_at} className="text-xs" />
          </div>
        </div>
      </div>

      {/* Book info */}
      {book && (
        <button
          onClick={() => book.id ? navigate(`/books/${book.id}`) : navigate(`/books/google/${book.google_books_id}`)}
          className="flex items-start gap-3 w-full text-left group mb-3"
        >
          <div className="w-12 h-16 flex-shrink-0 rounded overflow-hidden bg-cream-dark shadow-sm">
            {book.cover_url || book.thumbnail ? (
              <img
                src={book.cover_url || book.thumbnail}
                alt={book.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-ink-muted" />
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-ink group-hover:text-terra transition-colors font-heading leading-tight">
              {book.title}
            </p>
            <p className="text-xs text-ink-muted mt-0.5">{book.author || book.authors}</p>
          </div>
        </button>
      )}

      {/* Review content */}
      {item.type === 'review' && item.review && (
        <div className="mt-2 space-y-1.5">
          {item.review.star_rating > 0 && (
            <StarRating value={item.review.star_rating} readonly size="sm" />
          )}
          {item.review.body && (
            <p className="text-sm text-ink-light leading-relaxed line-clamp-4">
              {item.review.body}
            </p>
          )}
        </div>
      )}

      {/* Reaction content */}
      {item.type === 'reaction' && item.reaction && (
        <div className="mt-2 flex items-start gap-2">
          {item.reaction.emoji && (
            <span className="text-xl leading-none">{item.reaction.emoji}</span>
          )}
          {item.reaction.body && (
            <p className="text-sm text-ink-light leading-relaxed line-clamp-3">
              {item.reaction.body}
            </p>
          )}
        </div>
      )}

      {/* Discussion post preview */}
      {item.type === 'discussion' && item.discussion && (
        <div className="mt-2">
          {item.discussion.tag && item.discussion.tag !== 'no_spoilers' && (
            <span className={`inline-block mb-1.5 text-xs px-2 py-0.5 rounded-full border ${
              item.discussion.tag === 'spoilers'
                ? 'text-red-600 bg-red-50 border-red-200'
                : 'text-amber-700 bg-amber-50 border-amber-200'
            }`}>
              {item.discussion.tag === 'spoilers' ? 'Spoilers' : 'Midway'}
            </span>
          )}
          <p className="text-sm text-ink-light leading-relaxed line-clamp-2">
            {item.discussion.body}
          </p>
          <button
            onClick={() => item.book?.id ? navigate(`/books/${item.book.id}`) : navigate(`/books/google/${item.book?.google_books_id}`)}
            className="mt-2 text-xs text-terra hover:underline"
          >
            Join the discussion →
          </button>
        </div>
      )}

      {/* Discussion reply preview */}
      {item.type === 'discussion_reply' && item.discussion_reply && (
        <div className="mt-2">
          <p className="text-sm text-ink-light leading-relaxed line-clamp-2">
            {item.discussion_reply.body}
          </p>
          <button
            onClick={() => item.book?.id ? navigate(`/books/${item.book.id}`) : navigate(`/books/google/${item.book?.google_books_id}`)}
            className="mt-2 text-xs text-terra hover:underline"
          >
            View discussion →
          </button>
        </div>
      )}
    </motion.div>
  );
}

const ONBOARDING_STEPS = [
  { emoji: '📚', label: "Add books you've read" },
  { emoji: '👀', label: "Mark what you're reading now" },
  { emoji: '🔖', label: 'Save books you want to read' },
];

function OnboardingBanner({ shelfCount, onDismiss, onImport }) {
  const navigate = useNavigate();
  const booksNeeded = Math.max(0, 3 - shelfCount);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        className="bg-terra/5 border border-terra/20 rounded-2xl p-5 mb-6 relative"
      >
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 text-ink-muted hover:text-ink transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-terra mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-heading text-lg text-ink">Welcome to ReadTheRoom!</h3>
            <p className="text-sm text-ink-muted mt-0.5">
              {shelfCount === 0
                ? 'Start by adding at least 3 books to your shelf.'
                : `You've added ${shelfCount} book${shelfCount !== 1 ? 's' : ''}. Add ${booksNeeded} more to get started!`}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-4">
          {ONBOARDING_STEPS.map((step, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-ink-light">
              <span className="text-base">{step.emoji}</span>
              <span>{step.label}</span>
              {i < ONBOARDING_STEPS.length - 1 && (
                <span className="hidden sm:inline text-ink-muted/40 mx-1">·</span>
              )}
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 bg-cream-dark rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full bg-terra rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, (shelfCount / 3) * 100)}%` }}
            />
          </div>
          <span className="text-xs text-ink-muted shrink-0">{shelfCount}/3 books</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate('/search')}
            className="bg-terra hover:bg-terra/90 text-cream text-sm"
            size="sm"
          >
            <BookOpen className="w-4 h-4 mr-1.5" />
            Browse &amp; add books
          </Button>
          <Button
            onClick={onImport}
            variant="outline"
            size="sm"
            className="border-terra/30 text-terra hover:bg-terra/5 text-sm gap-1.5"
          >
            <span className="text-base leading-none">📚</span>
            Import from Goodreads
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function FeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feedItems, setFeedItems] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [popularBooks, setPopularBooks] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [loadingDiscovery, setLoadingDiscovery] = useState(true);
  const [shelfCount, setShelfCount] = useState(null); // null = loading
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const onboardingKey = user ? `rtr_onboarded_${user.id}` : null;
  const showOnboarding = !onboardingDismissed && shelfCount !== null && shelfCount < 3;

  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true);
    if (onboardingKey) localStorage.setItem(onboardingKey, '1');
  };

  useEffect(() => {
    if (!onboardingKey) return;
    if (localStorage.getItem(onboardingKey)) {
      setOnboardingDismissed(true);
      return;
    }
    shelfApi.getMyShelf()
      .then((items) => setShelfCount(items.length))
      .catch(() => setShelfCount(0));
  }, [onboardingKey]);

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const data = await feedApi.getFeed();
        setFeedItems(Array.isArray(data) ? data : data?.items || []);
      } catch (err) {
        toast.error('Could not load feed');
      } finally {
        setLoadingFeed(false);
      }
    };

    const fetchDiscovery = async () => {
      try {
        const [users, books] = await Promise.all([
          discoveryApi.getUsers(),
          discoveryApi.getBooks(),
        ]);
        setSuggestedUsers(Array.isArray(users) ? users.slice(0, 5) : []);
        setPopularBooks(Array.isArray(books) ? books.slice(0, 6) : []);
      } catch (_) {
        // Discovery is non-critical
      } finally {
        setLoadingDiscovery(false);
      }
    };

    fetchFeed();
    fetchDiscovery();
  }, []);

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <AppNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Left Sidebar */}
          <aside className="hidden lg:block lg:col-span-3">
            {/* Quick profile */}
            <div
              onClick={() => navigate(`/profile/${user.username}`)}
              className="bg-cream border border-cream-dark rounded-xl p-4 mb-6 cursor-pointer hover:border-terra/30 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={user.avatar_url} alt={user.display_name} />
                  <AvatarFallback className="bg-terra text-cream font-semibold">
                    {getInitials(user.display_name || user.username)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-ink text-sm">{user.display_name}</p>
                  <p className="text-xs text-ink-muted">@{user.username}</p>
                </div>
              </div>
              {user.bio && (
                <p className="text-xs text-ink-muted line-clamp-2">{user.bio}</p>
              )}
            </div>

            {/* Suggested users */}
            {suggestedUsers.length > 0 && (
              <div className="bg-cream border border-cream-dark rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-terra" />
                  <h3 className="text-sm font-semibold text-ink">Suggested Readers</h3>
                </div>
                <div className="space-y-1">
                  {suggestedUsers.map((u) => (
                    <UserCard key={u.username} user={u} />
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/search')}
                  className="w-full mt-2 text-terra hover:text-terra/80 text-xs"
                >
                  Discover more readers
                </Button>
              </div>
            )}
          </aside>

          {/* Center Feed */}
          <main className="lg:col-span-6">
            {showOnboarding && (
              <OnboardingBanner
                shelfCount={shelfCount}
                onDismiss={handleDismissOnboarding}
                onImport={() => setImportOpen(true)}
              />
            )}

            {/* Trending Books Carousel */}
            <TrendingBooks books={popularBooks} loading={loadingDiscovery} />

            {/* From the Culture — RSS */}
            <CultureFeedSection />

            {/* Activity Feed */}
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-terra" />
              <h3 className="text-sm font-semibold text-ink">Activity</h3>
            </div>

            {loadingFeed ? (
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => <FeedItemSkeleton key={i} />)}
              </div>
            ) : feedItems.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-cream-dark rounded-xl">
                <p className="text-ink-muted text-sm mb-3">
                  Follow readers to see their activity here
                </p>
                <Button
                  onClick={() => navigate('/search')}
                  variant="outline"
                  size="sm"
                  className="border-terra/30 text-terra hover:bg-terra/5 text-xs"
                >
                  <Users className="w-3.5 h-3.5 mr-1.5" />
                  Find Readers
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {feedItems.map((item, i) => (
                  <FeedItem key={item.id || i} item={item} />
                ))}
              </div>
            )}
          </main>

          {/* Right Discovery Sidebar */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="bg-cream border border-cream-dark rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <Compass className="w-4 h-4 text-terra" />
                <h3 className="text-sm font-semibold text-ink">Popular Books</h3>
              </div>
              {loadingDiscovery ? (
                <div className="grid grid-cols-3 gap-2">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="aspect-[2/3] rounded bg-cream-dark" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {popularBooks.map((book) => (
                    <BookCard
                      key={book.id || book.google_books_id}
                      book={book}
                      compact={false}
                    />
                  ))}
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/search')}
                className="w-full mt-4 text-terra hover:text-terra/80 text-xs"
              >
                Explore more books
              </Button>
            </div>
          </aside>
        </div>
      </div>

      <MobileBottomNav />

      <GoodreadsImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportDone={(action) => {
          setImportOpen(false);
          handleDismissOnboarding();
          // Re-fetch shelf count after import
          shelfApi.getMyShelf().then((items) => setShelfCount(items.length)).catch(() => {});
          if (action === 'shelf') navigate(`/profile/${user.username}`);
        }}
      />
    </div>
  );
}
